import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useDocumentStore } from '../store/documentStore';
import DocumentCard from '../components/DocumentCard';
import SearchBar from '../components/SearchBar';
import Modal from '../components/Modal';
import SkeletonLoader from '../components/SkeletonLoader';
import EmptyState from '../components/EmptyState';
import { useToast } from '../hooks/useToast';
import { getUserFriendlyErrorMessage } from '../utils/errorMessage';
import { ChevronLeft, ChevronRight, ArrowUpDown, FileText, CheckSquare, Square, Trash2, RefreshCw } from 'lucide-react';
import type { DocumentStatus, SearchFilters } from '../types';

type SortField = 'uploadDate' | 'fileName' | 'status';
type SortOrder = 'asc' | 'desc';
type ViewMode = 'all' | 'pending_only';

function parseCsvParam(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

function clampInt(value: string | null, fallback: number, min: number, max: number): number {
  if (!value) return fallback;
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

function parseNumberParam(value: string | null): number | undefined {
  if (value == null || value === '') return undefined;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : undefined;
}

function parseFieldFilters(values: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const raw of values) {
    const idx = raw.indexOf(':');
    if (idx <= 0) continue;
    const field = raw.slice(0, idx).trim();
    const val = raw.slice(idx + 1).trim();
    if (!field || !val) continue;
    out[field] = val;
  }
  return out;
}

function serializeFieldFilters(fieldFilters: Record<string, string> | undefined): string[] {
  if (!fieldFilters) return [];
  return Object.entries(fieldFilters)
    .filter(([k, v]) => k.trim() && String(v).trim())
    .map(([k, v]) => `${k}:${String(v)}`);
}

function sortFieldFromParam(value: string | null): SortField {
  if (value === 'fileName' || value === 'status' || value === 'uploadDate') return value;
  return 'uploadDate';
}

function sortOrderFromParam(value: string | null): SortOrder {
  return value === 'asc' ? 'asc' : 'desc';
}

function viewModeFromParam(value: string | null): ViewMode {
  return value === 'pending_only' ? 'pending_only' : 'all';
}

/** Align empty store filters with URL-parsed defaults (e.g. all_time when no upload-date range). */
function normalizeDatePresetForCompare(f: SearchFilters): string {
  if (
    f.datePreset === 'today' ||
    f.datePreset === 'this_week' ||
    f.datePreset === 'week' ||
    f.datePreset === 'month' ||
    f.datePreset === 'year'
  ) {
    return f.datePreset;
  }
  if (f.datePreset === 'custom') return 'custom';
  if (f.datePreset === 'all_time') return 'all_time';
  return f.dateFrom?.trim() && f.dateTo?.trim() ? 'custom' : 'all_time';
}

function areSearchFiltersEqual(a: SearchFilters, b: SearchFilters): boolean {
  const normalize = (f: SearchFilters) => ({
    query: f.query || '',
    dateFrom: f.dateFrom || '',
    dateTo: f.dateTo || '',
    vendor: f.vendor || '',
    amountMin: f.amountMin ?? null,
    amountMax: f.amountMax ?? null,
    datePreset: normalizeDatePresetForCompare(f),
    status: (f.status || []).slice().sort(),
    fieldFilters: Object.fromEntries(
      Object.entries(f.fieldFilters || {}).sort(([k1], [k2]) => k1.localeCompare(k2))
    ),
  });
  return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
}

/** Display label for YYYY-MM-DD upload filter bounds (UTC calendar day, matches API). */
function formatUploadBannerDate(isoDay: string): string {
  const s = isoDay.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(`${s}T12:00:00.000Z`);
  return d.toLocaleDateString(undefined, { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric' });
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const documentStore = useDocumentStore();
  const [hideFieldFilterWarning, setHideFieldFilterWarning] = useState(false);
  const {
    documents,
    loading,
    error,
    setSearchFilters,
    selectDocument,
    deleteDocument,
    bulkDeleteDocuments,
    restoreDocument,
    clearError,
    loadDocumentsPage,
    refreshDocumentsPage,
    nextTokenByPage,
    searchFilters,
  } = documentStore;
  const { showToast } = useToast();
  const isDeletedPage = location.pathname === '/deleted';
  const [selectedDeletedIds, setSelectedDeletedIds] = useState<Set<string>>(new Set());

  // Default view is "all documents" (pending + active); "Show only pending" and Deleted Items use explicit status filters
  const [showOnlyPending, setShowOnlyPending] = useState<boolean>(false);
  const effectiveView = showOnlyPending ? 'pending_only' : 'all';
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(12);
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('uploadDate');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  
  // Modal state
  const [modal, setModal] = useState<{
    isOpen: boolean;
    type: 'info' | 'success' | 'warning' | 'error' | 'confirm';
    title: string;
    message: string;
    onConfirm?: () => void;
    confirmText?: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const parseUrlToState = useCallback(() => {
    const urlQuery = (searchParams.get('q') || '').trim();
    const urlDateFrom = (searchParams.get('df') || '').trim();
    const urlDateTo = (searchParams.get('dt') || '').trim();
    const urlDatePreset = (searchParams.get('dp') || '').trim();
    const urlAmountMin = parseNumberParam(searchParams.get('amin'));
    const urlAmountMax = parseNumberParam(searchParams.get('amax'));

    const urlStatuses = parseCsvParam(searchParams.get('st')) as DocumentStatus[];
    const urlFieldFilters = parseFieldFilters(searchParams.getAll('ff'));

    const urlViewMode = viewModeFromParam(searchParams.get('v'));
    const urlPage = clampInt(searchParams.get('p'), 1, 1, 10000);
    const urlItemsPerPage = clampInt(searchParams.get('ipp'), 12, 1, 500);
    const urlSortField = sortFieldFromParam(searchParams.get('sf'));
    const urlSortOrder = sortOrderFromParam(searchParams.get('so'));

    // Status precedence:
    // - /deleted always shows deleted
    // - v=pending_only forces pending-only
    // - else, honor st (share exactly); default active+pending if absent
    let status: DocumentStatus[];
    if (isDeletedPage) status = ['deleted'];
    else if (urlViewMode === 'pending_only') status = ['pending'];
    else status = urlStatuses.length > 0 ? urlStatuses : (['pending', 'active'] as DocumentStatus[]);

    const parsedFilters: SearchFilters = {};
    if (urlQuery) parsedFilters.query = urlQuery.substring(0, 50);
    if (urlDateFrom) parsedFilters.dateFrom = urlDateFrom;
    if (urlDateTo) parsedFilters.dateTo = urlDateTo;

    const presetFromUrl = urlDatePreset as SearchFilters['datePreset'] | undefined;
    const hasBothUploadDates = !!(urlDateFrom && urlDateTo);
    if (hasBothUploadDates) {
      if (presetFromUrl === 'all_time') {
        parsedFilters.datePreset = 'custom';
      } else if (
        presetFromUrl === 'today' ||
        presetFromUrl === 'this_week' ||
        presetFromUrl === 'week' ||
        presetFromUrl === 'month' ||
        presetFromUrl === 'year' ||
        presetFromUrl === 'custom'
      ) {
        parsedFilters.datePreset = presetFromUrl;
      } else if (presetFromUrl) {
        parsedFilters.datePreset = 'custom';
      } else {
        parsedFilters.datePreset = 'custom';
      }
    } else if (
      presetFromUrl === 'today' ||
      presetFromUrl === 'this_week' ||
      presetFromUrl === 'week' ||
      presetFromUrl === 'month' ||
      presetFromUrl === 'year'
    ) {
      parsedFilters.datePreset = presetFromUrl;
    } else if (presetFromUrl === 'all_time') {
      parsedFilters.datePreset = 'all_time';
    } else {
      parsedFilters.datePreset = 'all_time';
    }
    if (urlAmountMin !== undefined) parsedFilters.amountMin = urlAmountMin;
    if (urlAmountMax !== undefined) parsedFilters.amountMax = urlAmountMax;
    if (Object.keys(urlFieldFilters).length > 0) parsedFilters.fieldFilters = urlFieldFilters;
    parsedFilters.status = status;

    return {
      filters: parsedFilters,
      viewMode: isDeletedPage ? 'all' : urlViewMode,
      page: urlPage,
      itemsPerPage: urlItemsPerPage,
      sortField: urlSortField,
      sortOrder: urlSortOrder,
    };
  }, [searchParams, isDeletedPage]);

  const updateUrl = useCallback((patch: (p: URLSearchParams) => void, opts?: { replace?: boolean }) => {
    const next = new URLSearchParams(searchParams);
    patch(next);
    setSearchParams(next, { replace: opts?.replace ?? false });
  }, [searchParams, setSearchParams]);

  const writeFiltersToUrl = useCallback((filters: SearchFilters) => {
    updateUrl((p) => {
      // Changing filters should reset pagination.
      p.set('p', '1');

      ['q', 'df', 'dt', 'dp', 'amin', 'amax', 'st'].forEach(k => p.delete(k));
      p.delete('ff');

      if (filters.query) p.set('q', filters.query);
      if (filters.datePreset !== 'all_time') {
        if (filters.dateFrom) p.set('df', filters.dateFrom);
        if (filters.dateTo) p.set('dt', filters.dateTo);
        if (filters.datePreset && filters.datePreset !== 'custom') {
          p.set('dp', String(filters.datePreset));
        }
      }
      if (filters.amountMin !== undefined) p.set('amin', String(filters.amountMin));
      if (filters.amountMax !== undefined) p.set('amax', String(filters.amountMax));
      if (filters.status && filters.status.length > 0) p.set('st', filters.status.join(','));

      for (const ff of serializeFieldFilters(filters.fieldFilters)) {
        p.append('ff', ff);
      }
    });
  }, [updateUrl]);

  // URL is the source of truth: parse URL -> store filters + local view state
  useEffect(() => {
    const parsed = parseUrlToState();

    if (!areSearchFiltersEqual(searchFilters, parsed.filters)) {
      setSearchFilters(parsed.filters);
    }

    const nextShowOnlyPending = parsed.viewMode === 'pending_only';
    if (showOnlyPending !== nextShowOnlyPending) setShowOnlyPending(nextShowOnlyPending);
    if (currentPage !== parsed.page) setCurrentPage(parsed.page);
    if (itemsPerPage !== parsed.itemsPerPage) setItemsPerPage(parsed.itemsPerPage);
    if (sortField !== parsed.sortField) setSortField(parsed.sortField);
    if (sortOrder !== parsed.sortOrder) setSortOrder(parsed.sortOrder);
    // Fetch this page of documents from backend
    loadDocumentsPage(parsed.page, parsed.itemsPerPage, parsed.filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parseUrlToState, setSearchFilters, loadDocumentsPage]);

  // If route constraints override status, normalize URL so shared links are exact.
  useEffect(() => {
    const urlViewMode = viewModeFromParam(searchParams.get('v'));
    const urlStatuses = parseCsvParam(searchParams.get('st'));
    const mustForceDeleted = isDeletedPage;
    const mustForcePendingOnly = !isDeletedPage && urlViewMode === 'pending_only';

    const desiredStatuses = mustForceDeleted ? ['deleted'] : mustForcePendingOnly ? ['pending'] : null;
    if (!desiredStatuses) return;

    const normalizedUrlStatuses = urlStatuses.length > 0 ? urlStatuses : [];
    const same =
      desiredStatuses.length === normalizedUrlStatuses.length &&
      desiredStatuses.every(s => normalizedUrlStatuses.includes(s));
    if (same) return;

    updateUrl((p) => p.set('st', desiredStatuses.join(',')), { replace: true });
  }, [isDeletedPage, searchParams, updateUrl]);

  // Show toast when document list fails to load
  useEffect(() => {
    if (error) {
      showToast('error', getUserFriendlyErrorMessage(error));
      clearError();
    }
  }, [error, showToast, clearError]);

  const handleView = (id: string) => {
    selectDocument(id);
    navigate(`/document/${id}`);
  };

  const handleEdit = (id: string) => {
    selectDocument(id);
    navigate(`/document/${id}?edit=true`);
  };

  const handleDelete = (id: string) => {
    const doc = documents.find(d => d.id === id);
    if (isDeletedPage) {
      // On deleted page, delete means permanent delete
      setModal({
        isOpen: true,
        type: 'confirm',
        title: 'Permanently Delete Document',
        message: `Are you sure you want to permanently delete "${doc?.fileName || 'this document'}"? This action cannot be undone.`,
        onConfirm: async () => {
          await deleteDocument(id, true);
        },
        confirmText: 'Permanently Delete',
      });
    } else {
      // On other pages, delete means soft delete
      setModal({
        isOpen: true,
        type: 'confirm',
        title: 'Delete Document',
        message: `Are you sure you want to move "${doc?.fileName || 'this document'}" to Deleted Items?`,
        onConfirm: async () => {
          await deleteDocument(id, false);
        },
        confirmText: 'Move to Deleted Items',
      });
    }
  };

  const handleRestore = async (id: string) => {
    await restoreDocument(id);
  };

  // Filter documents based on page type and "Show only pending" checkbox
  const filteredDocuments = useMemo(() => {
    if (isDeletedPage) {
      return documents.filter(doc => doc.status === 'deleted');
    }
    if (effectiveView === 'pending_only') {
      return documents.filter(doc => doc.status === 'pending');
    }
    return documents;
  }, [documents, effectiveView, isDeletedPage]);

  const selectedDeletedCount = selectedDeletedIds.size;
  const allDeletedOnPageSelected = useMemo(() => {
    if (!isDeletedPage || filteredDocuments.length === 0) return false;
    return filteredDocuments.every((doc) => selectedDeletedIds.has(doc.id));
  }, [isDeletedPage, filteredDocuments, selectedDeletedIds]);

  // Sort documents (within current page)
  const sortedDocuments = useMemo(() => {
    const sorted = [...filteredDocuments];
    sorted.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'uploadDate':
          aValue = new Date(a.uploadDate).getTime();
          bValue = new Date(b.uploadDate).getTime();
          break;
        case 'fileName':
          aValue = a.fileName.toLowerCase();
          bValue = b.fileName.toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredDocuments, sortField, sortOrder]);

  const pendingCount = filteredDocuments.filter(d => d.status === 'pending').length;

  const hasPrev = currentPage > 1;
  const hasNext = nextTokenByPage[currentPage + 1] !== null && nextTokenByPage[currentPage + 1] !== undefined;

  const handleSort = (field: SortField) => {
    const nextSortField = field;
    const nextSortOrder: SortOrder =
      sortField === field ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'desc';

    updateUrl((p) => {
      p.set('sf', nextSortField);
      p.set('so', nextSortOrder);
      p.set('p', '1');
    });
  };

  const handleRefresh = useCallback(async () => {
    const parsed = parseUrlToState();
    await refreshDocumentsPage(parsed.page, parsed.itemsPerPage, parsed.filters);
  }, [parseUrlToState, refreshDocumentsPage]);

  const uploadDateRangeBanner = useMemo(() => {
    const from = searchFilters.dateFrom?.trim();
    const to = searchFilters.dateTo?.trim();
    if (!from || !to) return null;
    return `Showing uploads from ${formatUploadBannerDate(from)} - ${formatUploadBannerDate(to)}`;
  }, [searchFilters.dateFrom, searchFilters.dateTo]);

  const activeFieldFilters = searchFilters.fieldFilters || {};
  const hasActiveFieldFilters = Object.keys(activeFieldFilters).length > 0;
  const showFieldFilterZeroResultsWarning =
    !loading && hasActiveFieldFilters && filteredDocuments.length === 0 && !hideFieldFilterWarning;

  const handleClearFieldFilters = () => {
    const { fieldFilters: _ignored, ...rest } = searchFilters;
    const nextFilters: SearchFilters = {
      ...rest,
    };
    writeFiltersToUrl(nextFilters);
    setSearchFilters(nextFilters);
  };

  const toggleDeletedSelection = (id: string, checked: boolean) => {
    setSelectedDeletedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleSelectAllDeletedOnPage = (checked: boolean) => {
    setSelectedDeletedIds((prev) => {
      const next = new Set(prev);
      for (const doc of filteredDocuments) {
        if (checked) next.add(doc.id);
        else next.delete(doc.id);
      }
      return next;
    });
  };

  const handleBulkPermanentDelete = () => {
    const ids = Array.from(selectedDeletedIds);
    if (ids.length === 0) return;
    setModal({
      isOpen: true,
      type: 'confirm',
      title: 'Permanently Delete Selected Documents',
      message: `Are you sure you want to permanently delete ${ids.length} selected document(s)? This action cannot be undone.`,
      onConfirm: async () => {
        const { successCount, failedIds } = await bulkDeleteDocuments(ids, true);
        setSelectedDeletedIds((prev) => {
          const next = new Set(prev);
          for (const id of ids) next.delete(id);
          return next;
        });
        if (failedIds.length > 0) {
          showToast(
            'warning',
            `Permanently deleted ${successCount} document(s). ${failedIds.length} failed; please retry.`
          );
        } else {
          showToast('success', `Permanently deleted ${successCount} document(s).`);
        }
      },
      confirmText: 'Permanently Delete Selected',
    });
  };

  useEffect(() => {
    if (!isDeletedPage && selectedDeletedIds.size > 0) {
      setSelectedDeletedIds(new Set());
      return;
    }
    if (isDeletedPage) {
      setSelectedDeletedIds((prev) => {
        if (prev.size === 0) return prev;
        const visibleDeletedIds = new Set(filteredDocuments.map((doc) => doc.id));
        const next = new Set<string>();
        for (const id of Array.from(prev)) {
          if (visibleDeletedIds.has(id)) next.add(id);
        }
        return next.size === prev.size ? prev : next;
      });
    }
  }, [isDeletedPage, filteredDocuments, selectedDeletedIds.size]);

  return (
    <div>
      <div className="mb-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-2">
          <div className="min-w-0">
            <h2 className="text-2xl font-bold text-gray-900">
              {isDeletedPage ? 'Deleted Items' : effectiveView === 'pending_only' ? 'Pending Documents' : 'Documents'}
            </h2>
            <p className="text-gray-600">
              {isDeletedPage
                ? 'View and restore deleted documents. Permanently delete items you no longer need.'
                : effectiveView === 'pending_only'
                ? 'Documents awaiting verification. Approve or reject to move them to active or deleted.'
                : 'Search and filter documents by any field. Showing active and pending documents.'}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2 text-sm text-gray-600">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 min-h-[44px] sm:min-h-0 sm:py-1.5"
              aria-label="Refresh document list"
            >
              <RefreshCw className={`h-4 w-4 shrink-0 ${loading ? 'animate-spin' : ''}`} aria-hidden />
              Refresh
            </button>
            <span className="inline-flex items-center min-h-[44px] sm:min-h-0">
              Page {currentPage}, showing {sortedDocuments.length} document(s)
            </span>
          </div>
        </div>
      </div>

      <SearchBar
        initialFilters={searchFilters}
        onSearch={(filters) => {
          const urlViewMode = viewModeFromParam(searchParams.get('v'));
          let status: DocumentStatus[];
          if (isDeletedPage) status = ['deleted'];
          else if (urlViewMode === 'pending_only') status = ['pending'];
          else status = (filters.status && filters.status.length > 0)
            ? filters.status
            : (parseCsvParam(searchParams.get('st')) as DocumentStatus[]);

          const nextFilters: SearchFilters = {
            ...filters,
            status: status.length > 0 ? status : (['pending', 'active'] as DocumentStatus[]),
          };

          writeFiltersToUrl(nextFilters);
          setSearchFilters(nextFilters);
        }}
      />

      {uploadDateRangeBanner && (
        <div
          className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800"
          role="status"
        >
          {uploadDateRangeBanner}
        </div>
      )}

      {/* Controls Bar */}
      <div className="mb-6 flex flex-col gap-4 bg-white rounded-lg shadow-sm p-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-3">
          {!isDeletedPage && (
            <label className="text-sm text-gray-600 flex items-center gap-2 cursor-pointer min-h-[44px] sm:min-h-0">
              <input
                type="checkbox"
                checked={showOnlyPending}
                onChange={(e) => {
                  const checked = e.target.checked;
                  updateUrl((p) => {
                    p.set('v', checked ? 'pending_only' : 'all');
                    p.set('p', '1');
                    if (checked) p.set('st', 'pending');
                  });
                }}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span>Show only pending</span>
            </label>
          )}
          <label className="text-sm text-gray-600 flex flex-wrap items-center gap-2 min-h-[44px] sm:min-h-0">
            <span>Items per page:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                const nextIpp = Number(e.target.value);
                updateUrl((p) => {
                  p.set('ipp', String(nextIpp));
                  p.set('p', '1');
                });
              }}
              className="border border-gray-300 rounded-md px-2 py-2 text-sm min-h-[44px] sm:min-h-0 sm:py-1"
            >
              <option value={6}>6</option>
              <option value={12}>12</option>
              <option value={24}>24</option>
              <option value={48}>48</option>
              <option value={96}>96</option>
            </select>
          </label>

          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <span className="w-full sm:w-auto">Sort by:</span>
            <button
              type="button"
              onClick={() => handleSort('uploadDate')}
              className={`flex items-center gap-1 rounded px-3 py-2 min-h-[44px] hover:bg-gray-100 sm:min-h-0 sm:px-2 sm:py-1 ${
                sortField === 'uploadDate' ? 'bg-blue-50 text-blue-600' : ''
              }`}
            >
              <span>Date</span>
              <ArrowUpDown className="w-3 h-3 shrink-0" />
            </button>
            <button
              type="button"
              onClick={() => handleSort('fileName')}
              className={`flex items-center gap-1 rounded px-3 py-2 min-h-[44px] hover:bg-gray-100 sm:min-h-0 sm:px-2 sm:py-1 ${
                sortField === 'fileName' ? 'bg-blue-50 text-blue-600' : ''
              }`}
            >
              <span>Name</span>
              <ArrowUpDown className="w-3 h-3 shrink-0" />
            </button>
            <button
              type="button"
              onClick={() => handleSort('status')}
              className={`flex items-center gap-1 rounded px-3 py-2 min-h-[44px] hover:bg-gray-100 sm:min-h-0 sm:px-2 sm:py-1 ${
                sortField === 'status' ? 'bg-blue-50 text-blue-600' : ''
              }`}
            >
              <span>Status</span>
              <ArrowUpDown className="w-3 h-3 shrink-0" />
            </button>
          </div>
        </div>
        {isDeletedPage && filteredDocuments.length > 0 && (
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <button
              type="button"
              onClick={() => toggleSelectAllDeletedOnPage(!allDeletedOnPageSelected)}
              className="inline-flex items-center justify-center px-3 py-2.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 min-h-[44px] sm:py-1.5"
            >
              {allDeletedOnPageSelected ? (
                <CheckSquare className="w-4 h-4 mr-1.5 shrink-0" />
              ) : (
                <Square className="w-4 h-4 mr-1.5 shrink-0" />
              )}
              {allDeletedOnPageSelected ? 'Clear Page Selection' : 'Select Page'}
            </button>
            <button
              type="button"
              onClick={handleBulkPermanentDelete}
              disabled={selectedDeletedCount === 0}
              className={`inline-flex items-center justify-center px-3 py-2.5 text-sm font-medium rounded-md min-h-[44px] sm:py-1.5 ${
                selectedDeletedCount === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              <Trash2 className="w-4 h-4 mr-1.5 shrink-0" />
              Permanently Delete Selected ({selectedDeletedCount})
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div aria-busy="true" aria-live="polite">
          <div className="mb-4 flex flex-col items-center justify-center gap-2 rounded-lg border border-primary-100 bg-primary-50/60 px-4 py-6 text-center">
            <div
              className="animate-spin rounded-full h-10 w-10 border-4 border-primary-200 border-t-primary-600"
              aria-hidden
            />
            <p className="text-sm font-medium text-gray-800">
              {searchFilters.query?.trim()
                ? 'Searching documents…'
                : 'Loading documents…'}
            </p>
            <p className="text-xs text-gray-500 max-w-md">
              {searchFilters.query?.trim()
                ? 'Please wait — uncommon terms can take several seconds to scan.'
                : 'Please wait while we load this page.'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <SkeletonLoader type="card" count={itemsPerPage} />
          </div>
        </div>
      ) : filteredDocuments.length === 0 ? (
        <>
          {showFieldFilterZeroResultsWarning && (
            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 text-sm mb-3">
                No documents currently match{' '}
                {Object.entries(activeFieldFilters)
                  .map(([field, value]) => `${field}: ${value}`)
                  .join(', ')}
                .
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleClearFieldFilters}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
                >
                  Clear these field filters
                </button>
                <button
                  type="button"
                  onClick={() => setHideFieldFilterWarning(true)}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-blue-300 text-blue-700 bg-white hover:bg-blue-50"
                >
                  Ignore for now
                </button>
              </div>
            </div>
          )}
          <EmptyState
            icon={FileText}
            title="No documents found"
            description={isDeletedPage
              ? "No deleted documents found. Deleted items will appear here."
              : effectiveView === 'pending_only'
              ? "No pending documents. New uploads will appear here for verification."
              : "Try adjusting your search filters or upload your first document."}
            action={isDeletedPage ? undefined : {
              label: "Upload Document",
              onClick: () => navigate('/upload')
            }}
          />
        </>
      ) : (
        <>
          {pendingCount > 0 && (
            <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 font-medium">
                {pendingCount} document(s) pending verification
              </p>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {sortedDocuments.map((doc) => (
              <div key={doc.id} className="relative">
                {isDeletedPage && (
                  <label className="absolute top-2 left-2 z-30 inline-flex items-center bg-white/90 rounded-md px-2 py-1 shadow-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedDeletedIds.has(doc.id)}
                      onChange={(e) => toggleDeletedSelection(doc.id, e.target.checked)}
                      className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                      aria-label={`Select ${doc.fileName} for permanent deletion`}
                    />
                  </label>
                )}
                <DocumentCard
                  document={doc}
                  onView={handleView}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onRestore={isDeletedPage ? handleRestore : undefined}
                  showRestore={isDeletedPage}
                />
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {(hasPrev || hasNext) && (
            <div className="mt-8 flex items-center justify-center space-x-2">
              <button
                onClick={() => updateUrl((p) => {
                  const current = clampInt(p.get('p'), currentPage, 1, 10000);
                  p.set('p', String(Math.max(1, current - 1)));
                })}
                disabled={!hasPrev}
                className={`px-4 py-2 rounded-lg border ${
                  !hasPrev
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <span className="px-2 text-gray-600 text-sm">Page {currentPage}</span>

              <button
                onClick={() => updateUrl((p) => {
                  const current = clampInt(p.get('p'), currentPage, 1, 10000);
                  p.set('p', String(current + 1));
                })}
                disabled={!hasNext}
                className={`px-4 py-2 rounded-lg border ${
                  !hasNext
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                }`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        onConfirm={modal.onConfirm}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        showCancel={modal.type === 'confirm'}
        confirmText={modal.confirmText || (modal.type === 'confirm' ? 'Move to Deleted Items' : 'OK')}
      />
    </div>
  );
};

export default Home;
